import { NetworkStatus } from '@apollo/client';
import { Box, Combobox, FormControl, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import { useAppUser, useName } from '@bechtel/ht-clients-common';
import { OperatorType, User } from '@bechtel/ht-types';
import { Field } from 'formik';
import { get } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useDebounce } from '~/hooks';
import { useSearchUser } from '~/hooks/use-search-user';
import { useSearchUserProject } from '~/hooks/use-search-user-projects';
import { TextFieldStyled } from './styled';
import { UserInfoProps } from './types';

export const FormControlLabel = ({ label, disabled = false }) => {
  return (
    <Box css={disabled ? 'opacity:0.5' : ''}>
      <Text family="openSans" color="primary.700" size="300">
        {label}
      </Text>
    </Box>
  );
};

export const ErrorField = ({ error }) => {
  return (
    <Box pt={{ sm: '200' }} pl={{ sm: '300' }}>
      <Text size="250" color="bechtel.red">
        {error}
      </Text>
    </Box>
  );
};

const EmployeeSearchItem = ({ item }) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} style={{ minWidth: '200px' }}>
      <Box flexDirection={{ sm: 'column' }}>
        <Box>{item.bun ? item.bun : item.user.bun}</Box>
      </Box>
    </Box>
  );
};
export const UserInfo: FC<UserInfoProps> = ({ isSupervisorDetail, formikProps, userDetail }) => {
  const { errors, setFieldValue, dirty, values, setFieldError } = formikProps;
  const { getFullName } = useName();
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const [selectedUser, setSelectedUser] = useState<User | null>(userDetail ?? null);
  const [searchText, setSearchText] = useState('');
  const { appUser } = useAppUser();

  const debouncedSearchText = useDebounce(searchText, 500);
  const debounceUserBunText = useDebounce('', 500);
  const {
    searchUser,
    data: searchUserData,
    loading: searchUserLoading,
    networkStatus: searchUserNetwrokStatus,
  } = useSearchUser();
  const {
    searchUserProject,
    data: searchUserProjectData,
    loading: searchUserProjectLoading,
    networkStatus: searchUserProjectNetwrokStatus,
  } = useSearchUserProject();

  useEffect(() => {
    if (selectedUser) {
      setFieldValue('supervisorId', selectedUser?.id);
      setFieldValue('supervisorFirstName', selectedUser?.firstName);
      setFieldValue('supervisorLastName', selectedUser?.lastName);
      setFieldValue('supervisorLobEmployeeId', selectedUser?.lobEmployeeId ?? '');
    }
  }, [selectedUser]);

  useEffect(() => {
    // this will check user already exist or not
    if (debounceUserBunText.length >= 2) {
      searchUser(
        {
          sortInput: [],
          filterInput: {
            or: [
              {
                field: 'bun',
                operator: OperatorType.EQUAL,
                value: debounceUserBunText,
              },
            ],
          },
        },
        (data) => {
          if (data && data.items.length > 0) {
            setFieldError('bun', 'User currently registered');
          }
        }
      );
    }
  }, [debounceUserBunText]);

  useEffect(() => {
    if (debouncedSearchText.length >= 2) {
      // this will search supervisor detail in user project
      searchUserProject({
        sortInput: [],
        filterInput: {
          and: [
            {
              field: 'user.bun',
              operator: OperatorType.LIKE,
              value: debouncedSearchText,
            },
            {
              field: 'projectId',
              operator: OperatorType.LIKE,
              value: appUser?.defaultProjectId,
            },
          ],
        },
      });
    }
  }, [debouncedSearchText]);

  const list = get(searchUserProjectData, 'searchUserProjects.items', []);
  return (
    <>
      <Box mb={{ sm: '600' }}>
        <Box flexBasis={{ sm: '46%' }} mr={{ sm: '500' }}>
          <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
            <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Last Name`} />}>
              <Field name={isSupervisorDetail ? 'supervisorLastName' : 'lastName'}>
                {({ field }) => {
                  return (
                    <TextFieldStyled autoFocus={true} disabled={isSupervisorDetail} color="primary.700" {...field} />
                  );
                }}
              </Field>
            </FormControl>
            {errors.lastName && dirty && !isSupervisorDetail ? <ErrorField error={errors.lastName} /> : null}
          </Box>
        </Box>
        <Box mr={{ sm: '500' }} flexBasis={{ sm: '52%' }}>
          <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
            <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`First Name`} />}>
              <Field name={isSupervisorDetail ? 'supervisorFirstName' : 'firstName'}>
                {({ field }) => {
                  return <TextFieldStyled disabled={isSupervisorDetail} color="primary.700" {...field} />;
                }}
              </Field>
            </FormControl>
            {errors.firstName && dirty && !isSupervisorDetail ? <ErrorField error={errors.firstName} /> : null}
          </Box>
        </Box>
      </Box>

      <Box mb={{ sm: '600' }}>
        <Box flexBasis={{ sm: '46%' }} mr={{ sm: '500' }}>
          <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
            <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Bun ID`} />}>
              <Field name={isSupervisorDetail ? 'supervisorBun' : 'bun'}>
                {({ field }) => {
                  if (isSupervisorDetail) {
                    return (
                      <Combobox
                        placeholder="Search supervisor"
                        size={TextFieldSize.LARGE}
                        items={list}
                        appearance={TextFieldAppearance.NORMAL}
                        itemString={itemToString}
                        itemComponent={EmployeeSearchItem}
                        selectedComponent={EmployeeSearchItem}
                        selectedItem={selectedUser}
                        onSelect={(selectedItem: any) => {
                          console.log(selectedItem);
                          //   setFieldValue('supervisorBun', selectedItem?.user?.bun, true);
                          setSelectedUser(selectedItem);
                        }}
                        term={searchText}
                        setTerm={setSearchText}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setSearchText(event.target.value);
                        }}
                        restorePreviousOnSkip
                        loading={searchUserProjectLoading || searchUserProjectNetwrokStatus === NetworkStatus.refetch}
                        messageOnNoResults={`No users found`}
                      />
                    );
                  } else {
                    return <TextFieldStyled color="primary.700" {...field} />;
                  }
                }}
              </Field>
            </FormControl>
            {!isSupervisorDetail && dirty && errors.bun ? <ErrorField error={errors.bun} /> : null}
            {isSupervisorDetail && dirty && errors.supervisorBun ? <ErrorField error={errors.supervisorBun} /> : null}
          </Box>
        </Box>
        <Box flexBasis={{ sm: '52%' }} mr={{ sm: '500' }}>
          <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
            <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`eTrack ID`} />}>
              <Field name={isSupervisorDetail ? 'supervisorLobEmployeeId' : 'lobEmployeeId'}>
                {({ field }) => {
                  return <TextFieldStyled disabled={isSupervisorDetail} color="primary.700" {...field} />;
                }}
              </Field>
            </FormControl>
            {!isSupervisorDetail && errors.lobEmployeeId && dirty ? <ErrorField error={errors.lobEmployeeId} /> : null}
          </Box>
        </Box>
      </Box>
      {!isSupervisorDetail && (
        <>
          <Box mb={{ sm: '600' }}>
            <Box flexBasis={{ sm: '46%' }} mr={{ sm: '500' }}>
              <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
                <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Tool Hound User ID`} />}>
                  <Field name="thUserId">
                    {({ field }) => {
                      return <TextFieldStyled disabled={true} {...field} style={{ opacity: 1 }} />;
                    }}
                  </Field>
                </FormControl>
              </Box>
            </Box>
            <Box flexBasis={{ sm: '52%' }} mr={{ sm: '500' }}>
              <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
                <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Toolhound Personnel ID`} />}>
                  <Field name="toolHoundId">
                    {({ field }) => {
                      return <TextFieldStyled {...field} disabled="true" />;
                    }}
                  </Field>
                </FormControl>
              </Box>
            </Box>
          </Box>
          <Box mb={{ sm: '500' }}>
            <Box flexBasis={{ sm: '46%' }} mr={{ sm: '500' }}>
              <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
                <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Job Title`} />}>
                  <Field name="persona">
                    {({ field }) => {
                      return <TextFieldStyled {...field} />;
                    }}
                  </Field>
                </FormControl>
                {dirty && errors.persona ? <ErrorField error={errors.persona} /> : null}
              </Box>
            </Box>
            <Box flexBasis={{ sm: '52%' }} mr={{ sm: '500' }}>
              <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
                <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`UPN`} />}>
                  <Field name="upn">
                    {({ field }) => {
                      return <TextFieldStyled {...field} />;
                    }}
                  </Field>
                </FormControl>
                {dirty && errors.upn ? <ErrorField error={errors.upn} /> : null}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};
